/**
 * Noor's PRODUCT IDENTITY: the name and the apex the Noor application (`apps/noor`) answers on.
 *
 * AN OVERLAY ON THE ACTIVE BRAND, NOT A SECOND BRAND (operator adjudication 2026-09-16, "identity overlay now"). The
 * tree has one active brand (`const/brand-registry.ts`) that the session cookie, the sign-in modal and every shared
 * component read; Noor is a product hosted under it with its own face. So this leaf carries what a face is -- the name
 * -- and nothing a brand is (legal name, cookie domain, a wire address). The day Noor becomes a brand of its own (its
 * own session) it enters `BRAND_REGISTRY` and this leaf is folded into that entry; until then the app reads this and
 * `ACTIVE_BRAND` for everything else.
 *
 * THE APEX IS HERE (operator, 2026-09-26: "the dev should be dev.noor.partners"), the tier `const/kenecting-identity.ts`
 * set: `noor.partners` is where the product answers, not a company on record, so it sits beside the name rather than
 * waiting on the brand entry. The GRAMMAR is the fleet's, read from the one composer (`apexHostnames` / `httpsOrigins`,
 * `const/brand-hostnames.ts`) over this apex, so nothing here re-spells it.
 *
 * WHAT NOOR IS FOR is a sentence, not a face, and it lives with the shell's copy (`NOOR_APP_COPY.purpose`,
 * `const/noor-app-copy.ts`). A tagline used to sit here as well; it had no reader and still described the two-sided
 * marketplace the product stopped being on 2026-09-16, so it was cut rather than reworded.
 *
 * THE BAR SETS THE NAME AS TEXT (operator, 2026-09-27: "replace noors logo with just text like we have for
 * kennecting"), in the bar-title type Kenecting's desk uses (`apps/noor/src/components/NoorWordmark.tsx`), so the face
 * is this leaf's name and nothing else: the drawn wordmark, its outline leaf and the inline-artwork shell that drew it
 * were cut with it. The MARK is still the site icon (`apps/noor/src/app/icon.svg`, the operator's file byte for byte,
 * which Next publishes at `/icon.svg`): the infinity alone, white on the ink tile. THE ICON FILE CARRIES NO COMMENT,
 * and this paragraph is why it needs none: a double hyphen inside an XML comment makes a browser refuse the whole
 * image, with a 200 behind it and every suite green. Its two colours are literals by necessity -- the expression's ink
 * (`COLOR_RAMPS.ink[900]`) and white -- and its sibling suite reads them off the ramp.
 */

import { apexHostnames, httpsOrigins } from './brand-hostnames.ts';
import type { WranglerEnvKey } from './config-environments.ts';

export const NOOR_PRODUCT_NAME = 'Noor';

/** The product's own apex -- a zone on the fleet's Cloudflare account, which the app's manifest attaches as each env's
 *  custom domain at deploy. */
export const NOOR_APEX = 'noor.partners';

/** The HOSTNAME the partner app answers on, per wrangler env block -- {@link apexHostnames} over this apex, the one
 *  spelling of the grammar and of what the manifest attaches. */
export const NOOR_APP_HOSTNAMES: Readonly<Record<WranglerEnvKey, string>> = apexHostnames(NOOR_APEX, null);

/** The public ORIGIN the app answers on, per wrangler env block -- what its manifest binds as `NEXT_PUBLIC_APP_URL`:
 *  the scheme over the hostname above, never a second spelling of it. */
export const NOOR_APP_URLS: Readonly<Record<WranglerEnvKey, string>> = httpsOrigins(NOOR_APP_HOSTNAMES);
